import {
  AnalysisResultsLiao,
  InputSampleCurves,
  NonNullableLiaoAnalysisResults,
  NonNullableWaterOilInputSampleCurves,
  NonNullableLiaoSampleResults,
  LiaoSampleResults,
  AnalysisResultsLambda,
  NonNullableLambdaAnalysisResults,
  LambdaSampleResults,
  NonNullableLambdaSampleResults
} from './@types/samples/types';

const DEFAULT_ARRAY = [];
const DEFAULT_NUMBER = 0;

export function parseLiaoAnalysisResultsToNonNullable(
  data: AnalysisResultsLiao
): NonNullableLiaoAnalysisResults {
  return {
    CorrectedSw: data.CorrectedSw ?? DEFAULT_ARRAY,
    CorrectedPc: data.CorrectedPc ?? DEFAULT_ARRAY,
    Pc_derivative: data.Pc_derivative ?? DEFAULT_ARRAY,
    PceMatch: data.PceMatch ?? data.Pres ?? DEFAULT_ARRAY,
    Pres: data.Pres ?? DEFAULT_ARRAY,
    PcD: data.PcD ?? DEFAULT_ARRAY,
    SwD: data.SwD ?? DEFAULT_ARRAY,
    x: data.x ?? DEFAULT_ARRAY,
    y: data.y ?? DEFAULT_ARRAY,
    height: data.height ?? DEFAULT_ARRAY
  };
}

export function parseLambdaAnalysisResultsToNonNullable(
  data: AnalysisResultsLambda
): NonNullableLambdaAnalysisResults {
  return {
    CorrectedSw: data.CorrectedSw ?? DEFAULT_ARRAY,
    CorrectedPc: data.CorrectedPc ?? DEFAULT_ARRAY,
    Pres: data.Pres ?? DEFAULT_ARRAY,
    PcD: data.PcD ?? DEFAULT_ARRAY,
    SwD: data.SwD ?? DEFAULT_ARRAY,
    ln_swn: data.ln_swn ?? DEFAULT_ARRAY,
    ln_pc: data.ln_pc ?? DEFAULT_ARRAY,
    ln_swn_squared: data.ln_swn_squared ?? DEFAULT_ARRAY,
    ln_pc_squared: data.ln_pc_squared ?? DEFAULT_ARRAY,
    ln_swn_x_ln_pc: data.ln_swn_x_ln_pc ?? DEFAULT_ARRAY,
    lambda_curve: data.lambda_curve ?? DEFAULT_ARRAY,
    indexes: data.indexes ?? DEFAULT_ARRAY
  };
}

export function parseSampleCurvesToNonNullable(
  data: InputSampleCurves
): NonNullableWaterOilInputSampleCurves {
  return {
    fw_lab: data.water_oil?.fw_lab || DEFAULT_ARRAY,
    kro_lab: data.water_oil?.kro_lab || DEFAULT_ARRAY,
    krw_lab: data.water_oil?.krw_lab || DEFAULT_ARRAY,
    sw_krel: data.water_oil?.sw_krel || DEFAULT_ARRAY,
    pc: data.pc_model?.pc || DEFAULT_ARRAY,
    sw: data.pc_model?.sw || DEFAULT_ARRAY
  };
}

export function parseLiaoSampleResultsToNonNullable(
  data: LiaoSampleResults
): NonNullableLiaoSampleResults {
  return {
    swir: data.swir ?? DEFAULT_NUMBER,
    pc_max: data.pc_max ?? DEFAULT_NUMBER,
    best_a: data.best_a ?? DEFAULT_NUMBER,
    best_b: data.best_b ?? DEFAULT_NUMBER,
    lambda_variable: data.lambda_variable ?? DEFAULT_NUMBER,
    true_swd: data.true_swd ?? DEFAULT_NUMBER,
    true_swir: data.true_swir ?? DEFAULT_NUMBER,
    true_pcd: data.true_pcd ?? DEFAULT_NUMBER,
    true_pc_max: data.true_pc_max ?? DEFAULT_NUMBER,
    fzi: data.fzi ?? DEFAULT_NUMBER,
    f_value: data.f_value ?? DEFAULT_NUMBER,
    pce: data.pce ?? DEFAULT_NUMBER,
    large_derivative: data.large_derivative ?? DEFAULT_NUMBER
  };
}

export function parseLambdaSampleResultsToNonNullable(
  data: LambdaSampleResults
): NonNullableLambdaSampleResults {
  return {
    ln_swn_sum: data.ln_swn_sum ?? DEFAULT_NUMBER,
    ln_pc_sum: data.ln_pc_sum ?? DEFAULT_NUMBER,
    ln_swn_squared_sum: data.ln_swn_squared_sum ?? DEFAULT_NUMBER,
    ln_pc_squared_sum: data.ln_pc_squared_sum ?? DEFAULT_NUMBER,
    lnswn_x_lnpc_sum: data.lnswn_x_lnpc_sum ?? DEFAULT_NUMBER,
    pe: data.pe ?? DEFAULT_NUMBER,
    lambda_value: data.lambda_value ?? DEFAULT_NUMBER
  };
}

/**
 * Extracts a human-readable message from an API error, normalizing the shapes
 * the backend can return:
 *  - FastAPI validation 422: `{ detail: [{ loc: [...,'porosity'], msg, type }] }`
 *  - Domain handler 400/500: `{ message: '...' }`
 *  - Anything else: the provided fallback (or the raw error message).
 */
export function getApiErrorMessage(error: unknown, fallback: string): string {
  const response = (error as any)?.response;
  const data = response?.data;

  if (data) {
    // FastAPI 422 validation envelope
    if (Array.isArray(data.detail)) {
      const lines = data.detail
        .map((item: any) => {
          const rawMsg = String(item?.msg ?? '').replace(/^Value error,\s*/i, '');
          const loc = Array.isArray(item?.loc) ? item.loc : [];
          // Last string segment of loc is the offending field name.
          const field = [...loc].reverse().find((seg: any) => typeof seg === 'string' && seg !== 'body');
          return field ? `${field}: ${rawMsg}` : rawMsg;
        })
        .filter(Boolean);
      if (lines.length > 0) return lines.join('\n');
    }

    // Domain handler error body
    if (typeof data.detail === 'string' && data.detail) return data.detail;
    if (typeof data.message === 'string' && data.message) return data.message;
  }

  if (typeof (error as any)?.message === 'string' && (error as any).message) {
    return (error as any).message;
  }

  return fallback;
}
